import React from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useCategory, useProductsByCategory } from "../hooks/useCatalog";
import { useSeo } from "../lib/seo";
import ProductCard from "../components/common/ProductCard";
import { SectionLabel } from "../components/common/Ornaments";

export default function Category() {
  const { slug } = useParams();
  const { data: category, isLoading: categoryLoading } = useCategory(slug);
  const { data: products = [], isLoading: productsLoading } = useProductsByCategory(slug);
  useSeo({
    title: category?.name,
    description: category?.description,
    path: `/category/${slug}`,
    image: category?.image,
  });

  if (categoryLoading || productsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="animate-pulse space-y-6">
          <div className="h-10 w-64 mx-auto bg-emerald/10 rounded" />
          <div className="h-4 w-96 max-w-full mx-auto bg-emerald/10 rounded" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-80 bg-emerald/10 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-4xl text-emerald">Category Not Found</h1>
        <p className="mt-3 text-emerald-light/80">The category you're looking for doesn't exist.</p>
        <Link to="/shop" className="mt-6 inline-block bg-emerald text-cream px-6 py-3 text-sm">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`category-page-${slug}`}>
      {/* Hero */}
      <section className="relative bg-emerald text-cream overflow-hidden">
        <img
          src={category.image}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald/70 via-emerald/85 to-emerald" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <div className="flex items-center justify-center gap-2 text-cream/70 text-xs">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight size={12} />
            <Link to="/shop" className="hover:text-gold">Shop</Link>
            <ChevronRight size={12} />
            <span className="text-gold">{category.name}</span>
          </div>
          <SectionLabel className="mt-6 justify-center"><span>{category.tagline}</span></SectionLabel>
          <h1 className="font-serif text-5xl md:text-6xl mt-4">
            {category.name.split(" ")[0]}{" "}
            <span className="italic text-gold">{category.name.split(" ").slice(1).join(" ")}</span>
          </h1>
          <p className="mt-4 text-cream/85 max-w-2xl mx-auto">{category.description}</p>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {products.length === 0 ? (
          <div className="text-center py-20">
            <div className="font-serif text-3xl text-emerald">Coming Soon</div>
            <p className="text-emerald-light/70 mt-2">
              We're curating hampers for this occasion. Chat with us on WhatsApp for a custom recommendation.
            </p>
          </div>
        ) : (
          <>
            <div className="text-sm text-emerald-light/70 mb-6">
              Showing <span className="font-semibold text-emerald">{products.length}</span> hampers
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
